import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["loop", "trapped", "onMountAutoFocus", "onUnmountAutoFocus"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _createForOfIteratorHelper(o, allowArrayLike) { var it = typeof Symbol !== "undefined" && o[Symbol.iterator] || o["@@iterator"]; if (!it) { if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") { if (it) o = it; var i = 0; var F = function F() {}; return { s: F, n: function n() { if (i >= o.length) return { done: true }; return { done: false, value: o[i++] }; }, e: function e(_e) { throw _e; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var normalCompletion = true, didErr = false, err; return { s: function s() { it = it.call(o); }, n: function n() { var step = it.next(); normalCompletion = step.done; return step; }, e: function e(_e2) { didErr = true; err = _e2; }, f: function f() { try { if (!normalCompletion && it.return != null) it.return(); } finally { if (didErr) throw err; } } }; }
function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }
function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i]; return arr2; }
import * as React from 'react';
import { focusScopesStack, removeLinks } from './focusScopesStack';
import { focus, focusFirst, getTabbableCandidates, getTabbableEdges } from './utils';
import { DomPrimitive } from '../DomPrimitive';
import { useCallbackRef } from '../../hooks/useCallbackRef';
import { useComposedRefs } from '../../hooks/useComposedRefs';
import { jsx as _jsx } from "react/jsx-runtime";
var AUTOFOCUS_ON_MOUNT = 'focusScope.autoFocusOnMount';
var AUTOFOCUS_ON_UNMOUNT = 'focusScope.autoFocusOnUnmount';
var EVENT_OPTIONS = {
  bubbles: false,
  cancelable: true
};
var FOCUS_SCOPE_NAME = 'FocusScope';
export var FocusScope = /*#__PURE__*/React.forwardRef(function (props, forwardedRef) {
  var _props$loop = props.loop,
    loop = _props$loop === void 0 ? false : _props$loop,
    _props$trapped = props.trapped,
    trapped = _props$trapped === void 0 ? false : _props$trapped,
    onMountAutoFocusProp = props.onMountAutoFocus,
    onUnmountAutoFocusProp = props.onUnmountAutoFocus,
    scopeProps = _objectWithoutProperties(props, _excluded);
  var _React$useState = React.useState(null),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    container = _React$useState2[0],
    setContainer = _React$useState2[1];
  var onMountAutoFocus = useCallbackRef(onMountAutoFocusProp);
  var onUnmountAutoFocus = useCallbackRef(onUnmountAutoFocusProp);
  var lastFocusedElementRef = React.useRef(null);
  var composedRefs = useComposedRefs(forwardedRef, function (node) {
    return setContainer(node);
  });
  var focusScope = React.useRef({
    paused: false,
    pause: function pause() {
      this.paused = true;
    },
    resume: function resume() {
      this.paused = false;
    }
  }).current;

  // Обеспечивает захват фокуса, если фокус перемещается программно
  React.useEffect(function () {
    // eslint-disable-next-line no-inner-declarations
    function handleFocusIn(event) {
      if (focusScope.paused || !container) {
        return;
      }
      var target = event.target;
      if (container.contains(target)) {
        lastFocusedElementRef.current = target;
      } else {
        focus(lastFocusedElementRef.current, {
          select: true
        });
      }
    }

    // eslint-disable-next-line no-inner-declarations
    function handleFocusOut(event) {
      if (focusScope.paused || !container) {
        return;
      }
      var relatedTarget = event.relatedTarget;

      // Событие `focusout` с `null` в `relatedTarget` произойдет как минимум в двух случаях:
      //
      // 1. Когда пользователь переключает приложение/вкладки/окна/сам браузер теряет фокус.
      // 2. В Google Chrome, когда фокусированный элемент удаляется из DOM.
      //
      // Мы позволяем браузеру делать свое дело здесь, потому что:
      //
      // 1. Браузер уже запоминает, что было в фокусе, когда страница снова получает фокус.
      // 2. В Google Chrome, если мы попытаемся сфокусироваться на удаленном элементе (как указано ниже), это
      //    приводит к загрузке ЦП на 100%, поэтому мы избегаем этого по этой причине.
      if (relatedTarget === null) {
        return;
      }

      // Если фокус переместился на фактический допустимый элемент (`relatedTarget !== null`)
      // который находится за пределами контейнера, мы перемещаем фокус на последний допустимый фокусированный элемент внутри.
      if (!container.contains(relatedTarget)) {
        focus(lastFocusedElementRef.current, {
          select: true
        });
      }
    }

    // Когда фокусированный элемент удаляется из DOM, браузеры перемещают фокус
    // обратно на document.body. В этом случае мы перемещаем фокус на контейнер
    // чтобы правильно захватить фокус.
    // eslint-disable-next-line no-inner-declarations
    function handleMutations(mutations) {
      var focusedElement = document.activeElement;
      if (focusedElement !== document.body) {
        return;
      }
      var _iterator = _createForOfIteratorHelper(mutations),
        _step;
      try {
        for (_iterator.s(); !(_step = _iterator.n()).done;) {
          var mutation = _step.value;
          if (mutation.removedNodes.length > 0) {
            focus(container);
          }
        }
      } catch (err) {
        _iterator.e(err);
      } finally {
        _iterator.f();
      }
    }
    var mutationObserver;
    if (trapped) {
      document.addEventListener('focusin', handleFocusIn);
      document.addEventListener('focusout', handleFocusOut);
      var _mutationObserver = new MutationObserver(handleMutations);
      if (container) {
        _mutationObserver.observe(container, {
          childList: true,
          subtree: true
        });
      }
    }
    return function () {
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
      mutationObserver === null || mutationObserver === void 0 || mutationObserver.disconnect();
    };
  }, [trapped, container, focusScope.paused]);
  React.useEffect(function () {
    if (container) {
      focusScopesStack.add(focusScope);
      var previouslyFocusedElement = document.activeElement;
      var hasFocusedCandidate = container.contains(previouslyFocusedElement);
      if (!hasFocusedCandidate) {
        var mountEvent = new CustomEvent(AUTOFOCUS_ON_MOUNT, EVENT_OPTIONS);
        container.addEventListener(AUTOFOCUS_ON_MOUNT, onMountAutoFocus);
        container.dispatchEvent(mountEvent);
        if (!mountEvent.defaultPrevented) {
          focusFirst(removeLinks(getTabbableCandidates(container)), {
            select: true
          });
          if (document.activeElement === previouslyFocusedElement) {
            focus(container);
          }
        }
      }
      return function () {
        container.removeEventListener(AUTOFOCUS_ON_MOUNT, onMountAutoFocus);

        // Мы столкнулись с ошибкой React (исправлено в v17) с фокусировкой при размонтировании.
        // Нам нужно немного задержать фокус, чтобы обойти это на данный момент.
        // См.: https://github.com/facebook/react/issues/17894
        setTimeout(function () {
          var unmountEvent = new CustomEvent(AUTOFOCUS_ON_UNMOUNT, EVENT_OPTIONS);
          container.addEventListener(AUTOFOCUS_ON_UNMOUNT, onUnmountAutoFocus);
          container.dispatchEvent(unmountEvent);
          if (!unmountEvent.defaultPrevented) {
            focus(previouslyFocusedElement !== null && previouslyFocusedElement !== void 0 ? previouslyFocusedElement : document.body, {
              select: true
            });
          }
          // нам нужно удалить слушатель после `dispatchEvent`
          container.removeEventListener(AUTOFOCUS_ON_UNMOUNT, onUnmountAutoFocus);
          focusScopesStack.remove(focusScope);
        }, 0);
      };
    }
    return;
  }, [container, onMountAutoFocus, onUnmountAutoFocus, focusScope]);

  // Обеспечивает зацикливание фокуса (при переходе по табуляции на краях)
  var handleKeyDown = React.useCallback(function (event) {
    if (!loop && !trapped) {
      return;
    }
    if (focusScope.paused) {
      return;
    }
    var isTabKey = event.key === 'Tab' && !event.altKey && !event.ctrlKey && !event.metaKey;
    var focusedElement = document.activeElement;
    if (isTabKey && focusedElement) {
      var _container = event.currentTarget;
      var _getTabbableEdges = getTabbableEdges(_container),
        _getTabbableEdges2 = _slicedToArray(_getTabbableEdges, 2),
        first = _getTabbableEdges2[0],
        last = _getTabbableEdges2[1];
      var hasTabbableElementsInside = first && last;

      // мы можем зациклить фокус только если у нас есть табулируемые края
      if (!hasTabbableElementsInside) {
        if (focusedElement === _container) {
          event.preventDefault();
        }
      } else {
        if (!event.shiftKey && focusedElement === last) {
          event.preventDefault();
          if (loop) {
            focus(first, {
              select: true
            });
          }
        } else if (event.shiftKey && focusedElement === first) {
          event.preventDefault();
          if (loop) {
            focus(last, {
              select: true
            });
          }
        }
      }
    }
  }, [loop, trapped, focusScope.paused]);
  return /*#__PURE__*/_jsx(DomPrimitive.div, _objectSpread(_objectSpread({
    tabIndex: -1
  }, scopeProps), {}, {
    ref: composedRefs,
    onKeyDown: handleKeyDown
  }));
});
FocusScope.displayName = FOCUS_SCOPE_NAME;